const raw = import.meta.env.VITE_WORKER_URL || '';
export const apiBaseUrl = raw.replace(/\/+$/, '');

export async function apiFetch(path, init = {}) {
  if (!apiBaseUrl) {
    throw new Error('VITE_WORKER_URL is not configured');
  }
  const url = `${apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    ...init,
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const message =
      (data && (data.error || data.message)) || res.statusText || 'Request failed';
    throw new Error(message);
  }
  return data;
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

export function getHealth() {
  return apiFetch('/api/health');
}

export async function getRoles() {
  const data = await apiFetch('/api/roles');
  return data?.roles ?? [];
}

export async function updateRole(id, patch) {
  const data = await apiFetch(`/api/roles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  return data?.role ?? null;
}

export async function getQuestions(roleId, sessionType = 'weekly') {
  const qs = new URLSearchParams({ type: sessionType });
  const data = await apiFetch(`/api/questions/${roleId}?${qs.toString()}`);
  return data?.questions ?? [];
}

export async function createQuestion(roleId, questionText, sessionType = 'weekly') {
  const data = await apiFetch('/api/questions', {
    method: 'POST',
    body: JSON.stringify({
      role_id: roleId,
      question_text: questionText,
      session_type: sessionType,
    }),
  });
  return data?.question ?? null;
}

export async function deleteQuestion(id) {
  return await apiFetch(`/api/questions/${id}`, { method: 'DELETE' });
}

export async function createSession({ sessionType, weekStartDate, notes }) {
  const data = await apiFetch('/api/sessions', {
    method: 'POST',
    body: JSON.stringify({
      session_type: sessionType,
      week_start_date: weekStartDate,
      notes: notes ?? null,
    }),
  });
  return data?.session ?? null;
}

export async function listSessions({ type, limit = 10, offset = 0 } = {}) {
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  const data = await apiFetch(`/api/sessions?${params.toString()}`);
  return data?.sessions ?? [];
}

export async function getSession(id) {
  const data = await apiFetch(`/api/sessions/${id}`);
  return { session: data?.session ?? null, answers: data?.answers ?? [] };
}

export async function updateSession(id, patch) {
  const data = await apiFetch(`/api/sessions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  return data?.session ?? null;
}

export async function createAnswer({ sessionId, roleId, questionId, answerText }) {
  const data = await apiFetch('/api/answers', {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      role_id: roleId,
      question_id: questionId,
      answer_text: answerText ?? null,
    }),
  });
  return data?.answer ?? null;
}

export async function updateAnswer(id, patch) {
  const data = await apiFetch(`/api/answers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  return data?.answer ?? null;
}

export async function transcribeVoice(r2Key) {
  const data = await apiFetch('/api/ai/transcribe', {
    method: 'POST',
    body: JSON.stringify({ r2_key: r2Key }),
  });
  return { transcript: data?.transcript ?? null, voiceNote: data?.voice_note ?? null };
}

export async function getSessionTranscripts(sessionId) {
  const data = await apiFetch(`/api/ai/transcripts/${sessionId}`);
  return data?.voice_notes ?? [];
}

export async function summariseSession(sessionId) {
  const data = await apiFetch('/api/ai/summarise', {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId }),
  });
  return {
    session: data?.session ?? null,
    debrief: data?.debrief ?? null,
    preview: data?.preview ?? null,
  };
}

export async function quarterlyReview(sessionId) {
  const data = await apiFetch('/api/ai/quarterly', {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId }),
  });
  return {
    session: data?.session ?? null,
    quarterlySummary: data?.quarterly_summary ?? null,
  };
}

// ---------- Calendar ----------

export async function getCalendarEvents({ from, to, roleId, eventType } = {}) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (roleId != null) params.set('role_id', String(roleId));
  if (eventType) params.set('event_type', eventType);
  const qs = params.toString();
  const data = await apiFetch(`/api/calendar${qs ? `?${qs}` : ''}`);
  return data?.events ?? [];
}

export async function createCalendarEvent(payload) {
  const data = await apiFetch('/api/calendar', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return data?.event ?? null;
}

export async function updateCalendarEvent(id, patch) {
  const data = await apiFetch(`/api/calendar/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  return data?.event ?? null;
}

export async function deleteCalendarEvent(id) {
  return await apiFetch(`/api/calendar/${id}`, { method: 'DELETE' });
}

// ---------- Weekly tasks ----------

export async function getTasks(sessionId) {
  const data = await apiFetch(`/api/tasks?session_id=${encodeURIComponent(sessionId)}`);
  return data?.tasks ?? [];
}

export async function createTask({ sessionId, roleId, title }) {
  const data = await apiFetch('/api/tasks', {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId, role_id: roleId ?? null, title }),
  });
  return data?.task ?? null;
}

export async function updateTask(id, patch) {
  const data = await apiFetch(`/api/tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  return data?.task ?? null;
}

export async function deleteTask(id) {
  return await apiFetch(`/api/tasks/${id}`, { method: 'DELETE' });
}

// ---------- Perfect Year ----------

export async function getPerfectYearAgenda() {
  const data = await apiFetch('/api/perfect-year');
  return { categories: data?.categories ?? [], items: data?.items ?? [] };
}

export async function getPerfectYearRuns(quarter) {
  const qs = quarter ? `?quarter=${encodeURIComponent(quarter)}` : '';
  const data = await apiFetch(`/api/perfect-year/runs${qs}`);
  return data?.runs ?? [];
}

export async function upsertPerfectYearRun({ itemId, quarter, outcomeNotes, completed }) {
  const body = { item_id: itemId, quarter };
  if (outcomeNotes !== undefined) body.outcome_notes = outcomeNotes;
  if (completed !== undefined) body.completed = completed;
  const data = await apiFetch('/api/perfect-year/runs', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data?.run ?? null;
}

export async function updatePerfectYearRun(id, patch) {
  const data = await apiFetch(`/api/perfect-year/runs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  return data?.run ?? null;
}

export async function deletePerfectYearRun(id) {
  return await apiFetch(`/api/perfect-year/runs/${id}`, { method: 'DELETE' });
}

// ---------- Settings ----------

export async function getSettings() {
  const data = await apiFetch('/api/settings');
  return data?.settings ?? {};
}

export async function updateSettings(patch) {
  const data = await apiFetch('/api/settings', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  return data?.settings ?? {};
}

export async function uploadVoice({ sessionId, roleId, answerId, blob, filename = 'voice.webm' }) {
  if (!apiBaseUrl) throw new Error('VITE_WORKER_URL is not configured');
  const form = new FormData();
  form.append('file', blob, filename);
  form.append('session_id', String(sessionId));
  form.append('role_id', String(roleId));
  if (answerId != null) form.append('answer_id', String(answerId));
  const res = await fetch(`${apiBaseUrl}/api/voice/upload`, { method: 'POST', body: form });
  const text = await res.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = { raw: text }; }
  }
  if (!res.ok) {
    throw new Error((data && (data.error || data.message)) || res.statusText || 'Upload failed');
  }
  return data?.voice_note ?? null;
}
